import {
  AuthFailure,
  NeedsOnlineLoginError,
  NetworkError,
  WeakPinError,
  WrongPinError,
} from '@assist/offline';
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { Field } from '../components/Field.js';
import { app } from '../controller.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { Icon, Mark } from '../ui/icons.js';

function authMessage(e: unknown): string {
  if (e instanceof NetworkError)
    return `${e.message}. The first sign-in on a device needs the internet.`;
  if (e instanceof AuthFailure) {
    if (e.code === 'locked') return 'Too many failed attempts. Wait 15 minutes and try again.';
    if (e.code === 'device_revoked') return 'This device has been revoked by an administrator.';
    return e.message || 'Wrong username or password';
  }
  return e instanceof Error ? e.message : String(e);
}

/** The signed-out screens own the page, as in ASSIST-MNH: brand panel left, the form right. */
function AuthLayout({ children }: { children: ComponentChildren }) {
  return (
    <div class="login">
      <div class="login-brand">
        <div class="lb-top">
          <span class="lb-mark">
            <Mark size={22} />
          </span>
          <span class="lb-name">{t.appName}</span>
        </div>
        <div class="lb-body">
          <h1>{t.loginHeadline}</h1>
          <p class="lb-full">{t.loginSub}</p>
          <ul class="lb-points">
            {t.loginPoints.map(([icon, head, body]) => (
              <li key={head}>
                <Icon k={icon} size={20} />
                <span>
                  <b>{head}</b> {body}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p class="lb-foot">{t.loginFoot}</p>
      </div>
      <div class="login-form">{children}</div>
    </div>
  );
}

export function SignIn() {
  const a = useApp();
  const [username, setU] = useState('');
  const [password, setP] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(a.notice);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await app.signIn(username.trim(), password);
    } catch (x) {
      setErr(authMessage(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <form class="lf-card" onSubmit={submit}>
        <h2>{t.signInTitle}</h2>
        <p class="lf-sub">{t.signInHelp}</p>
        <Field label={t.username} for="u">
          <input
            id="u"
            autoComplete="username"
            autoCapitalize="none"
            value={username}
            onInput={(e) => setU(e.currentTarget.value)}
            required
          />
        </Field>
        <Field label={t.password} for="p">
          <div class="pwwrap">
            <input
              id="p"
              type={show ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onInput={(e) => setP(e.currentTarget.value)}
              required
            />
            <button type="button" class="pwt" onClick={() => setShow(!show)}>
              {show ? t.hidePassword : t.showPassword}
            </button>
          </div>
        </Field>
        {err ? <p class="alert">{err}</p> : null}
        <button type="submit" class="primary" disabled={busy}>
          {busy ? t.signingIn : t.signIn}
        </button>
        {a.users.length ? (
          <button type="button" class="link" onClick={() => app.lock()}>
            {t.back}
          </button>
        ) : null}
      </form>
    </AuthLayout>
  );
}

export function SetPin() {
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: Event) => {
    e.preventDefault();
    if (pin !== again) return setErr(t.pinMismatch);
    setBusy(true);
    setErr(null);
    try {
      await app.setPin(pin);
    } catch (x) {
      setErr(x instanceof WeakPinError ? x.message : x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <form class="lf-card" onSubmit={submit}>
        <h2>{t.pinTitle}</h2>
        <p class="lf-sub">{t.pinHelp}</p>
        <Field label={t.pin} for="pin">
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={12}
            value={pin}
            onInput={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
            required
          />
        </Field>
        <Field label={t.pinAgain} for="pin2">
          <input
            id="pin2"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={12}
            value={again}
            onInput={(e) => setAgain(e.currentTarget.value.replace(/\D/g, ''))}
            required
          />
        </Field>
        {err ? <p class="alert">{err}</p> : null}
        <button type="submit" class="primary" disabled={busy}>
          {t.savePin}
        </button>
      </form>
    </AuthLayout>
  );
}

export function Unlock() {
  const a = useApp();
  const [userId, setUserId] = useState(a.users.length === 1 ? (a.users[0]?.userId ?? '') : '');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await app.unlock(userId, pin);
    } catch (x) {
      setPin('');
      if (x instanceof WrongPinError) setErr(x.message);
      else if (x instanceof NeedsOnlineLoginError) {
        app.notice = x.message;
        app.toSignIn();
      } else setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <form class="lf-card" onSubmit={submit}>
        <h2>{t.unlockTitle}</h2>
        <div class="users">
          {a.users.map((u) => (
            <button
              type="button"
              key={u.userId}
              class={u.userId === userId ? 'user on' : 'user'}
              onClick={() => {
                setUserId(u.userId);
                setErr(null);
              }}
            >
              <strong>{u.fullName}</strong>
              <span class="muted">{u.username}</span>
            </button>
          ))}
        </div>
        {userId ? (
          <>
            <Field label={t.pin} for="pin">
              <input
                id="pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                value={pin}
                onInput={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
                required
              />
            </Field>
            {err ? <p class="alert">{err}</p> : null}
            <button type="submit" class="primary" disabled={busy || pin.length < 6}>
              {t.unlock}
            </button>
          </>
        ) : null}
        <button type="button" class="link" onClick={() => app.toSignIn()}>
          {t.otherUser}
        </button>
      </form>
    </AuthLayout>
  );
}

export function Revoked() {
  return (
    <AuthLayout>
      <div class="lf-card">
        <h2>{t.revokedTitle}</h2>
        <p class="lf-sub">{t.revokedHelp}</p>
      </div>
    </AuthLayout>
  );
}
